import React from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  Box,
  Button,
  HStack,
  Text,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NotificationList from './NotificationList';
import type { NotificationItem } from '../../types/notification';

/**
 * Desktop popover shell for the bell: takes the trigger element as children and
 * renders the header ("read all"), the list, and a "view all" footer. Kept
 * separate from NotificationBell so the bell component stays focused.
 */
export default function NotificationPopover({
  isOpen,
  onClose,
  trigger,
  items,
  isLoading,
  isError,
  onRetry,
  onItemOpen,
  onItemToggleRead,
  onItemArchive,
  status,
  onStatusChange,
  onReadAll,
}: {
  isOpen: boolean;
  onClose: () => void;
  trigger: React.ReactElement;
  items: NotificationItem[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  /** Opens the row (role-resolved target owned by the caller). */
  onItemOpen: (item: NotificationItem) => void;
  /** Toggles read/unread only. Must never navigate. */
  onItemToggleRead: (item: NotificationItem) => void;
  onItemArchive: (item: NotificationItem) => void;
  status: 'all' | 'unread' | 'archived';
  onStatusChange: (s: 'all' | 'unread' | 'archived') => void;
  onReadAll: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement="bottom-end">
      <PopoverTrigger>{trigger}</PopoverTrigger>
      <PopoverContent w="400px" maxW="400px">
        <PopoverHeader p={0}>
          <HStack justify="space-between" px={3} py={2}>
            <Text fontWeight="semibold" fontSize="sm">
              {t('notification.title')}
            </Text>
            <Button size="xs" variant="ghost" onClick={onReadAll}>
              {t('notification.readAll')}
            </Button>
          </HStack>
        </PopoverHeader>
        <PopoverBody p={0}>
          <NotificationList
            items={items}
            isLoading={isLoading}
            isError={isError}
            onRetry={onRetry}
            onItemOpen={onItemOpen}
            onItemToggleRead={onItemToggleRead}
            onItemArchive={onItemArchive}
            status={status}
            onStatusChange={onStatusChange}
            maxHeight="400px"
          />
        </PopoverBody>
        <Box p={2} borderTopWidth="1px">
          <Button
            size="sm"
            w="full"
            variant="ghost"
            onClick={() => {
              onClose();
              navigate('/notifications');
            }}
          >
            {t('notification.viewAll')}
          </Button>
        </Box>
      </PopoverContent>
    </Popover>
  );
}
